import { zodResolver } from '@hookform/resolvers/zod'
import { t } from 'i18next'
import { useForm, FormProvider } from 'react-hook-form'

import { stepsFlow } from '@/features/proposta/brasil/domain/Comprador/steps'
import { RepresentanteLegalUseCase } from '@/features/proposta/brasil/domain/Comprador/use-case/representante-legal-use-case'
import type { CompradorPessoaFisicaStepSchemaOutput } from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import type {
  CompradorPessoaJuridicaSchemaInput,
  CompradorPessoaJuridicaSchemaOutput,
} from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pj.schema'
import type { TClienteCompleto } from '@/features/proposta/brasil/hooks/use-orquestrar-criacao-edicao'
import { useStepManager } from '@/features/proposta/brasil/hooks/use-stepper-proposta'
import { ContatosComprador } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/contatos-comprador'
import { DadosCompradorPJ } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/dados-comprador-pj'
import { DadosProcuradorEmpresa } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/dados-procurador'
import { DadosRepresentanteLegal } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/dados-representante-legal'
import { EstadoCivilVinculo } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/estado-civil-vinculo'
import { TipoResponsavelPJ } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/tipo-responsavel-pj'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'
import { StepCompradorLayout } from '@/features/proposta/shared/components/etapa-comprador/step-comprador-layout'
import type { TNacionalidade, TProfissao } from '@/types/cliente'

type TCadastroCompradorPJProps = {
  mode: 'create' | 'edit'
  onCancel: () => void
  onSubmit: (data: CompradorPessoaJuridicaSchemaOutput, isDirty: boolean) => Promise<void>
  nacionalidades: TNacionalidade[]
  profissoes: TProfissao[]
  compradores: TCompradoresCadastrados
  clienteJuridicoToEdit: TClienteCompleto | null
  emailsJaVinculados: Record<string, string>
}

export function CadastroCompradorPJ({
  mode,
  onCancel,
  onSubmit,
  nacionalidades,
  clienteJuridicoToEdit,
  profissoes,
  compradores,
  emailsJaVinculados,
}: Readonly<TCadastroCompradorPJProps>) {
  const { schema, conditionalSteps } = stepsFlow.compradorPessoaJuridica

  const methods = useForm<CompradorPessoaJuridicaSchemaInput, unknown, CompradorPessoaJuridicaSchemaOutput>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      emailsJaVinculados,
      mode: mode,
      tipo: 'PessoaJuridica',
      fluxoIndependente: null,
      dados: {
        buscouCnpj: mode === 'edit',
        cnpj: clienteJuridicoToEdit?.documento ?? '',
        email: clienteJuridicoToEdit?.email ?? '',
        razaoSocial: clienteJuridicoToEdit?.nome ?? '',
        id: clienteJuridicoToEdit?.id ?? undefined,
        telefone: clienteJuridicoToEdit?.telefone ?? { origem: 'Brasil', numero: '', principal: true },
        emailsJaVinculados,
      },
      responsavel: clienteJuridicoToEdit
        ? null
        : RepresentanteLegalUseCase.iniciarDefaultValues(emailsJaVinculados, nacionalidades[0]?.id),
    },
  })
  const isDirty = methods.formState.isDirty
  const { steps, idx, onNext, onBack, onFinish, isLast } = useStepManager(
    conditionalSteps,
    () => methods.getValues(),
    onCancel,
    onSubmit,
    isDirty,
  )

  const currentStep = steps[idx]
  const handleOnFinish = async (
    output: CompradorPessoaFisicaStepSchemaOutput | CompradorPessoaJuridicaSchemaOutput,
  ) => {
    await onFinish(output as CompradorPessoaJuridicaSchemaOutput)
  }

  const renderContent = () => {
    switch (currentStep) {
      case 'dadosPJ':
        return (
          <DadosCompradorPJ
            compradores={compradores}
            onBack={onBack}
            onNext={onNext}
            nacionalidadePrincipalId={nacionalidades[0]?.id}
            isLast={isLast}
            onFinish={handleOnFinish}
            emailsJaVinculados={emailsJaVinculados}
          />
        )
      case 'tipoResponsavel':
        return <TipoResponsavelPJ onBack={onBack} onNext={onNext} />
      case 'dadosRepresentanteLegal':
        return (
          <DadosRepresentanteLegal
            compradores={compradores}
            nacionalidades={nacionalidades}
            onBack={onBack}
            onNext={onNext}
            profissoes={profissoes}
          />
        )
      case 'dadosProcurador':
        return (
          <DadosProcuradorEmpresa
            compradores={compradores}
            nacionalidades={nacionalidades}
            onBack={onBack}
            onNext={onNext}
            profissoes={profissoes}
            emailsJaVinculados={emailsJaVinculados}
          />
        )
      case 'contatosRepresentanteLegal':
      case 'contatosProcurador':
        return (
          <ContatosComprador
            fieldName="responsavel.contato"
            titulo={t('proposta:cadastro_compradores.steps.contatos.responsavel_titulo', {
              responsavel: currentStep === 'contatosRepresentanteLegal' ? 'Representante Legal' : 'Procurador',
            })}
            onBack={onBack}
            onNext={onNext}
            isLast={isLast}
            onFinish={(e) => handleOnFinish(e as CompradorPessoaJuridicaSchemaOutput)}
          />
        )
      case 'estadoCivilRepresentanteLegal':
      case 'estadoCivilProcurador':
        return (
          <EstadoCivilVinculo
            fieldPath="responsavel.estadoCivil"
            isLast={isLast}
            onNext={onNext}
            onBack={onBack}
            onFinish={(e) => handleOnFinish(e as CompradorPessoaJuridicaSchemaOutput)}
          />
        )

      default:
        return null
    }
  }

  return (
    <FormProvider {...methods}>
      <StepCompradorLayout steps={steps} current={idx}>
        {renderContent()}
      </StepCompradorLayout>
    </FormProvider>
  )
}
